import { LoadingContainer } from 'app/components/LoadingContainer';
import { StyledStack } from 'app/components/Stack';
import StyledButton from 'app/components/StyledButton';
import useGetApiData from 'app/hooks/useGetApiData';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import Countdown from 'app/pages/TicketRefund/components/Countdown';
import {
  cancelUpgradeTicketApi,
  recoverCancelUpgrade,
} from 'app/services/checkTicketApi';
import { getDetailOrderApi } from 'app/services/checkoutApi';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { formatNumber } from 'utils/helper';
import useCountDown from 'utils/hooks/useCountDown';
import { Layout } from '../../layouts';
import { ACCOUNT_MNG_MENU } from '../../layouts/AccountManager';
import { CommonText, StyledDivider } from '../styled';
import ListGiftTicket from './_ListGiftTicket';
import ListRefundTicket from './_ListRefundTicket';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import * as _ from 'lodash';
const UpgradeTicket = () => {
  const { id }: { id: string } = useParams();
  const location = useLocation();
  const historyState: any = location?.state;
  const translations = useSelector(selectTranslations);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { data, isLoading, setData } = useGetApiData({
    api: getDetailOrderApi,
    params: id,
  });
  const { isLoading: isLoadingRecover, handleRequest: handleRequestRecover } =
    useGetApiData({
      api: recoverCancelUpgrade,
      isSkip: true,
    });
  const { isLoading: isLoadingCancel, handleRequest: handleRequestCancel } =
    useGetApiData({
      api: cancelUpgradeTicketApi,
      isSkip: true,
    });

  const history = useHistory();
  const { totalAmount, totalCost } = calculateTotals(data?.blockItems || []);

  const handleCancelTickets = useCallback(
    async (idTicket?: string) => {
      const formData = new FormData();
      if (!executeRecaptcha) return;
      const tokenCaptcha = await executeRecaptcha('createOrder');
      formData.append('captcha', tokenCaptcha);
      formData.append('order_id', data?.id);
      const listTicket = [...data?.blockItems];
      const listRecover = !!idTicket
        ? listTicket.filter(ticket => ticket.id === idTicket)
        : listTicket;
      const listCancel = !!idTicket
        ? listTicket.filter(ticket => ticket.id !== idTicket)
        : [];
      listRecover.length > 0 &&
        listRecover.forEach((item: any, index: number) => {
          formData.append(`recoverItems[${index}][item_id]`, item.id);
        });
      listCancel.length > 0 &&
        listCancel.forEach((item: any, index: number) => {
          formData.append(`cancelItems[${index}][item_id]`, item.id);
        });
      handleRequestRecover(formData).then(resData => {
        if (resData?.blockItems.length < 1) {
          history.push('/check-tickets');
          return;
        }
        if (!!resData?.idHash) {
          setData(resData);
        }
      });
    },
    [
      data?.blockItems,
      data?.id,
      executeRecaptcha,
      handleRequestRecover,
      history,
      setData,
    ],
  );

  const handleCancelSO = useCallback(async () => {
    if (!executeRecaptcha) return;
    const tokenCaptcha = await executeRecaptcha();
    const formData = new FormData();
    formData.append('captcha', tokenCaptcha);
    formData.append('idHash', id);
    handleRequestCancel(formData).then(() => {
      if (!!historyState?.exchangeResponse) {
        history.push('/check-tickets');
      } else {
        history.push({
          pathname: '/account',
          state: {
            targetTab: 'tab-3',
          },
        });
      }
    });
  }, [
    executeRecaptcha,
    handleRequestCancel,
    history,
    historyState?.exchangeResponse,
    id,
  ]);

  const handleRemove = useCallback(() => {
    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content: translations?.['b2b.upgrade.confirm-cancel'] ?? '',
      closable: false,
      buttons: [
        {
          text: translations?.['b2b.txt.back'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.btn.cancel'] ?? '',
          bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
          color: 'white',
          onClick: () => {
            handleCancelSO();
            onCloseDialogNoti();
          },
        },
      ],
    });
  }, [handleCancelSO, onCloseDialogNoti, onOpenDialogNoti, translations]);

  const timeDistance = useMemo(() => {
    return (
      moment(data?.expiredAt?.datetimeDMYHIS, 'HH:mm:ss DD/MM/YYYY').valueOf() -
      moment(data?.startTime?.datetimeDMYHIS, 'HH:mm:ss DD/MM/YYYY').valueOf()
    );
  }, [data?.expiredAt?.datetimeDMYHIS, data?.startTime?.datetimeDMYHIS]);

  const { timeRemaining, resetCountDown } = useCountDown({
    time: timeDistance,
    onTimeout: () => {
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: '',
        content:
          translations?.['b2b.txt.refundOrderExpried']?.replace(
            '{orderNumber}',
            data?.id,
          ) ?? '',
        closable: true,
        maskClosable: false,
        onCloseDialog: () => {
          onCloseDialogNoti();
          history.push('/account', {
            targetTab: 'tab-3',
          });
        },
      });
    },
  });

  useEffect(() => {
    resetCountDown(timeDistance);
  }, [resetCountDown, timeDistance]);

  const listGiftFormat = useMemo(() => {
    return data?.blockGiftItem?.reduce((a, b) => {
      return [
        ...a,
        ...b.tickets?.map(ticket => ({
          ...ticket,
          ...b,
        })),
      ];
    }, []);
  }, [data?.blockGiftItem]);

  return (
    <Layout menuActive={ACCOUNT_MNG_MENU.CHECK_TICKET}>
      <LoadingContainer
        loading={isLoading || isLoadingRecover || isLoadingCancel}
      >
        <StyledStack direction={'column'} rowGap={'24px'}>
          <StyledStack justifyContent={'space-between'}>
            <CommonText
              style={{
                fontSize: 20,
                color: '#000',
                fontWeight: 600,
                lineHeight: '24px',
              }}
            >
              {translations?.['b2b.upgrade.list-exchange-ticket']}
            </CommonText>
            <StyledStack
              display={{ lg: 'flex', xs: 'none' }}
              style={{ width: 'fit-content' }}
            >
              {_.isInteger(timeRemaining) && <Countdown time={timeRemaining} />}
            </StyledStack>
          </StyledStack>
          <CommonText style={{ fontWeight: 600, fontSize: 16 }}>
            {translations?.['b2b.upgrade.order'] + ' ' + data?.id}
          </CommonText>
          <CommonText style={{ fontStyle: 'italic', fontWeight: '400' }}>
            {'* ' + translations?.['b2b.upgrade.note']}
          </CommonText>
          <StyledStack display={{ lg: 'none', xs: 'flex' }}>
            {_.isInteger(timeRemaining) && <Countdown time={timeRemaining} />}
          </StyledStack>

          {listGiftFormat?.length > 0 && (
            <>
              <CommonText style={{ fontWeight: 600, fontSize: 16 }}>
                {translations?.['b2b.refund.order-gift']}
              </CommonText>
              <ListGiftTicket dataSource={listGiftFormat} />
            </>
          )}
          <CommonText style={{ fontWeight: 600, fontSize: 16 }}>
            {translations?.['b2b.upgrade.list-refund']}
          </CommonText>
          <ListRefundTicket
            dataSource={data?.blockItems}
            handleCancelTickets={handleCancelTickets}
          />
          <StyledStack direction={'column'} rowGap={'12px'}>
            <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
              <CommonText>
                {translations?.['b2b.upgrade.total-ticket']}
              </CommonText>
              <CommonText>{totalAmount}</CommonText>
            </StyledStack>
            <StyledDivider dashed />
            <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
              <CommonText>
                {translations?.['b2b.upgrade.total-ticket-cost']}
              </CommonText>
              <CommonText style={{ color: '#E92227' }}>
                {formatNumber(totalCost).replaceAll(',', '.') + 'đ'}
              </CommonText>
            </StyledStack>
            <StyledDivider dashed />
            <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
              <CommonText>
                {translations?.['b2b.upgrade.cost-convert']}
              </CommonText>
              <CommonText style={{ color: '#E92227' }}>
                {formatNumber(
                  totalCost - Number(data?.refundAmount),
                ).replaceAll(',', '.') + 'đ'}{' '}
              </CommonText>
            </StyledStack>
            <StyledDivider dashed />
            <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
              <CommonText style={{ fontSize: 16 }}>
                {translations?.['b2b.upgrade.total-refund']}
              </CommonText>
              <CommonText style={{ fontSize: 16, color: '#E92227' }}>
                {formatNumber(data?.refundAmount).replaceAll(',', '.') + 'đ'}
              </CommonText>
            </StyledStack>
          </StyledStack>
          <StyledDivider />
          <StyledStack justifyContent={'center'} columnGap={'16px'}>
            <StyledButton
              bgColor="#ffffff"
              onClick={handleRemove}
              style={{
                border: '1px solid #808080',
                color: '#808080',
                background: 'transparent',
              }}
            >
              {translations?.['b2b.btn.cancel']}
            </StyledButton>
            <StyledButton
              bgColor="#f06f24"
              onClick={() =>
                history.push(`/check-tickets/select-tickets/${id}`)
              }
              style={{
                color: 'white',
                backgroundImage: 'linear-gradient(to right, #f06f24, #F8B117)',
              }}
            >
              {translations?.['b2c.txt.continue']}
            </StyledButton>
          </StyledStack>
        </StyledStack>
      </LoadingContainer>
    </Layout>
  );
};

export default UpgradeTicket;

const calculateTotals = tickets => {
  let totalAmount = 0;
  let totalCost = 0;

  tickets.forEach(ticket => {
    totalAmount += ticket.amount;
    totalCost += ticket.price;
  });

  return {
    totalAmount: totalAmount,
    totalCost: totalCost,
  };
};

const disableStyle: React.CSSProperties = {
  pointerEvents: 'none',
  filter: 'contrast(0)',
  cursor: 'no-drop',
};
